// Task Service
// Complete task management with multi-protocol support

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  limit,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { atpRecordService } from './atpRecordService';
import { populateATPResult } from './executionTrustService';
import { taskTemplateService } from './taskTemplateService';
import { protocolService } from './protocolService';
import { riskService } from './riskService';
import { userGroupService } from './userGroupService';
import { roomService, departmentService } from './firebaseService';
import auditService from './auditService';
import { getEffectiveCriticality } from './riskClassificationAssignmentService';
import { SCHEDULE_TYPE_RISK_DRIVEN } from '../utils/scheduleRuleShape';
import { RISK_CLASSIFICATION_FREQUENCY_BUCKETS } from '../utils/riskClassificationConstants';

/**
 * Remove undefined values from an object (shallow). Firestore rejects undefined.
 */
function removeUndefined(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/** Default English labels for risk-driven frequency codes (task display). */
const RISK_DRIVEN_FREQUENCY_LABELS = Object.fromEntries(
  RISK_CLASSIFICATION_FREQUENCY_BUCKETS.map(b => [b.code, b.code.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())])
);

export class TaskService {
  constructor() {
    this.tasksCollection = collection(db, 'task_instances');
    this.logsCollection = collection(db, 'task_execution_log');
  }
  
  // ==================== TASK GENERATION ====================
  
  /**
   * Create task from template
   */
  async createTaskFromTemplate(templateId, customContext, assignedTo, assignedToName, scheduledStart, userId) {
    try {
      // Generate task data from template
      const taskData = await taskTemplateService.generateTaskFromTemplate(
        templateId,
        customContext,
        assignedTo,
        assignedToName,
        scheduledStart,
        userId
      );
      
      // Calculate base RPN and aggregate risk data from all protocols
      let baseRPN = 0;
      const allRisks = [];
      const allBiologicalEntities = new Set();
      
      for (const protocol of taskData.protocols) {
        try {
          const protocolRPN = await riskService.calculateProtocolTotalRPN(protocol.protocolId);
          baseRPN += protocolRPN;
          
          // Get all risks for this protocol
          const protocolRisks = await riskService.getProtocolRisks(protocol.protocolId);
          allRisks.push(...protocolRisks);
          
          // Collect biological entities
          protocolRisks.forEach(risk => {
            if (risk.biological_entities && Array.isArray(risk.biological_entities)) {
              risk.biological_entities.forEach(entity => {
                const entityId = entity.biologicalEntityId || entity.id;
                if (entityId) {
                  allBiologicalEntities.add(entityId);
                }
              });
            }
          });
        } catch (error) {
          console.warn(`Could not calculate RPN for protocol ${protocol.protocolId}:`, error);
        }
      }
      
      // Calculate aggregate risk statistics
      const rpnValues = allRisks.map(r => r.rpn || 0);
      const maxRPN = rpnValues.length > 0 ? Math.max(...rpnValues) : 0;
      const avgRPN = rpnValues.length > 0 
        ? rpnValues.reduce((sum, rpn) => sum + rpn, 0) / rpnValues.length 
        : 0;
      const criticalRisks = allRisks.filter(r => r.rpn >= 500);
      
      // Add risk data structure
      taskData.baseRPN = baseRPN;
      taskData.currentRPN = baseRPN;
      taskData.riskLevel = this.getRiskLevelFromRPN(baseRPN);
      taskData.risk_data = {
        max_rpn: maxRPN,
        avg_rpn: Math.round(avgRPN * 100) / 100,
        risk_count: allRisks.length,
        critical_risks: criticalRisks.map(r => ({
          id: r.id,
          scenario: r.riskScenario,
          rpn: r.rpn,
          criticality: r.criticality
        })),
        biological_entities: Array.from(allBiologicalEntities).map(entityId => {
          // Find entity details from any risk
          for (const risk of allRisks) {
            if (risk.biological_entities && Array.isArray(risk.biological_entities)) {
              const entity = risk.biological_entities.find(e => 
                (e.biologicalEntityId || e.id) === entityId
              );
              if (entity) {
                return {
                  id: entityId,
                  name: entity.biologicalEntityName || entity.name || 'Unknown'
                };
              }
            }
          }
          return { id: entityId, name: 'Unknown' };
        })
      };
      
      // Create task instance
      const docRef = await addDoc(this.tasksCollection, taskData);
      
      // Log task creation
      await this.logAction(docRef.id, userId, 'CREATE_TASK', null, null, {
        templateId,
        protocolCount: taskData.protocols.length,
        baseRPN
      });
      
      console.log(`✅ Created task from template: ${docRef.id}`);
      return docRef.id;
    } catch (error) {
      console.error('Error creating task from template:', error);
      throw error;
    }
  }
  
  /**
   * Create task manually (without template)
   */
  async createTask(taskData, userId) {
    try {
      const now = Timestamp.now();
      
      const task = {
        ...taskData,
        templateId: null,
        templateName: null,
        packageId: null,
        packageName: null,
        sequenceOrder: null,
        escalated: false,
        triggeredBy: 'manual',
        triggeredByUserId: userId,
        triggeredByEvent: null,
        triggeredByWorkflow: null,
        complianceScore: null,
        completionQuality: null,
        deviations: [],
        createdBy: userId,
        createdAt: now,
        updatedBy: userId,
        updatedAt: now
      };
      
      const docRef = await addDoc(this.tasksCollection, task);
      
      await this.logAction(docRef.id, userId, 'CREATE_TASK', null, null, {
        manual: true,
        protocolCount: task.protocols.length
      });
      
      console.log(`✅ Created manual task: ${docRef.id}`);
      return docRef.id;
    } catch (error) {
      console.error('Error creating manual task:', error);
      throw error;
    }
  }
  
  /**
   * Build task data from the protocol entity that generates the task (action, step, or protocol).
   * Single source of truth: all task details come from that entity + protocol context for display.
   */
  buildTaskDataFromProtocolEntity(protocol, generatorLevel, step, action, customContext, scheduledStart, userId) {
    const now = Timestamp.now();
    const entity = generatorLevel === 'action' ? action : (generatorLevel === 'step' ? step : protocol);
    // Protocol/step/action can have assignmentRules as array (first rule) or single object (e.g. protocol-level "Assign to group")
    const raw = entity.assignmentRules;
    const assignmentRules = (Array.isArray(raw) ? raw[0] : (raw && typeof raw === 'object' ? raw : null)) || {};
    const durationMinutes = entity.estimatedDuration ?? (generatorLevel === 'protocol' ? (protocol.totalEstimatedDuration || 30) : 30);
    const scheduledStartTime = scheduledStart && (scheduledStart.toMillis ? scheduledStart.toDate() : new Date(scheduledStart));
    const scheduledEndTime = new Date(scheduledStartTime.getTime());
    scheduledEndTime.setMinutes(scheduledEndTime.getMinutes() + durationMinutes);

    const taskName = entity.name || protocol.name || 'Protocol task';
    const taskDescription = entity.description || protocol.description || '';

    // Map a single action to payload shape
    const mapAction = (a) => ({
      actionId: a.id || a.name,
      actionName: a.name,
      description: a.description || '',
      estimatedDuration: a.estimatedDuration ?? null,
      equipment: (a.equipment || []).map(e => (typeof e === 'string' ? e : (e?.name || e))),
      supplies: (a.supplies || []).map(sup => (typeof sup === 'string' ? sup : (sup?.name || sup))),
      verificationMethod: a.verificationMethod || null,
      documentationFields: a.documentationFields || [],
      status: 'pending',
      completed: false
    });
    // Map a step to payload shape; for action-level pass single action to embed (otherwise all step actions)
    const mapStep = (s, singleAction = null) => ({
      stepId: s.id || s.name,
      stepName: s.name,
      status: 'pending',
      startTime: null,
      endTime: null,
      duration: null,
      expectedDuration: (s.estimatedDuration || 0) * 60,
      isOnTime: true,
      isComplete: false,
      isSkipped: false,
      skipReason: null,
      actions: singleAction ? [mapAction(singleAction)] : (s.actions || []).map(mapAction),
      notes: null,
      photos: [],
      documentation: null
    });

    // Atomize: task contains only the step(s) or step+action that generated it, not the whole protocol
    let stepsPayload;
    if (generatorLevel === 'action' && step && action) {
      stepsPayload = [mapStep(step, action)];
    } else if (generatorLevel === 'step' && step) {
      stepsPayload = [mapStep(step)];
    } else {
      stepsPayload = (protocol.steps || []).map(s => mapStep(s));
    }

    const protocolPayload = {
      protocolId: protocol.id,
      protocolName: protocol.name,
      protocolVersion: protocol.version || '1.0',
      sequenceOrder: 1,
      status: 'pending',
      generatorLevel,
      generatorStepId: step ? (step.id || step.name) : null,
      generatorStepName: step ? step.name : null,
      generatorActionId: action ? (action.id || action.name) : null,
      generatorActionName: action ? action.name : null,
      clinicalContexts: protocol.clinicalContexts || [],
      applicableDepartments: protocol.applicableDepartments || [],
      applicabilityRules: protocol.applicabilityRules || [],
      schedulingRules: protocol.schedulingRules || [],
      steps: stepsPayload,
      startTime: null,
      endTime: null,
      duration: null,
      complianceScore: null
    };

    return {
      name: taskName,
      description: taskDescription,
      taskType: 'protocol_task',
      templateId: null,
      templateName: null,
      packageId: null,
      packageName: null,
      sequenceOrder: null,
      triggeredBy: (customContext && customContext.workflowExecutionId) ? 'workflow' : 'manual',
      triggeredByUserId: userId,
      triggeredByEvent: null,
      triggeredByWorkflow: customContext?.workflowExecutionId || null,
      protocols: [protocolPayload],
      context: {
        ...(customContext || {}),
        protocolId: protocol.id,
        protocolName: protocol.name,
        stepId: step ? (step.id || step.name) : null,
        stepName: step ? step.name : null,
        actionId: action ? (action.id || action.name) : null,
        actionName: action ? action.name : null
      },
      assignedTo: assignmentRules.assignedToUser || null,
      assignedToName: assignmentRules.assignedToUserName || '',
      assignedRole: (assignmentRules.requiredRoles && assignmentRules.requiredRoles[0]) || '',
      assignmentType: assignmentRules.assignmentType || 'direct',
      assignedAt: now,
      acknowledgedAt: null,
      assignedToGroup: assignmentRules.assignedToGroup || null,
      assignedToGroupName: assignmentRules.assignedToGroupName || null,
      scheduledStart: Timestamp.fromDate(scheduledStartTime),
      scheduledEnd: Timestamp.fromDate(scheduledEndTime),
      actualStart: null,
      actualEnd: null,
      status: 'assigned',
      priority: entity.taskPriority || 'medium',
      urgency: entity.taskUrgency || 'routine',
      currentProtocolIndex: 0,
      currentStepIndex: 0,
      complianceScore: null,
      completionQuality: null,
      deviations: [],
      escalated: false,
      baseRPN: 0,
      currentRPN: 0,
      riskLevel: 'low',
      createdBy: userId,
      createdAt: now,
      updatedBy: userId,
      updatedAt: now,
      equipment: entity.equipment || [],
      supplies: entity.supplies || [],
      documentationFields: entity.documentationFields || [],
      verificationMethod: entity.verificationMethod || null
    };
  }

  /**
   * Create one task from protocol entity (action, step, or protocol), add risk data, write to Firestore.
   * assignmentOverride: optional { assignedToGroup, assignedTo, assignedToName } from workflow activity.
   */
  async createTaskFromProtocolEntity(protocol, generatorLevel, step, action, customContext, scheduledStart, userId, assignmentOverride = null) {
    let taskData = this.buildTaskDataFromProtocolEntity(protocol, generatorLevel, step, action, customContext, scheduledStart, userId);
    if (assignmentOverride) {
      if (assignmentOverride.assignedToGroup != null) taskData.assignedToGroup = assignmentOverride.assignedToGroup;
      if (assignmentOverride.assignedTo != null) taskData.assignedTo = assignmentOverride.assignedTo;
      if (assignmentOverride.assignedToName != null) taskData.assignedToName = assignmentOverride.assignedToName;
    }
    // Phase 5: resolve Risk-Driven schedule from location criticality + nomenclature
    const resolvedSchedule = await this._resolveRiskDrivenSchedule(protocol, customContext || {});
    if (resolvedSchedule) {
      taskData.resolvedRiskDrivenSchedule = resolvedSchedule;
      if (taskData.protocols && taskData.protocols[0]) {
        taskData.protocols[0].resolvedRiskDrivenSchedule = resolvedSchedule;
      }
    }
    let baseRPN = 0;
    const allRisks = [];
    const allBiologicalEntities = new Set();
    for (const p of taskData.protocols) {
      try {
        const protocolRPN = await riskService.calculateProtocolTotalRPN(p.protocolId);
        baseRPN += protocolRPN;
        const protocolRisks = await riskService.getProtocolRisks(p.protocolId);
        allRisks.push(...protocolRisks);
        protocolRisks.forEach(risk => {
          if (risk.biological_entities && Array.isArray(risk.biological_entities)) {
            risk.biological_entities.forEach(entity => {
              const entityId = entity.biologicalEntityId || entity.id;
              if (entityId) allBiologicalEntities.add(entityId);
            });
          }
        });
      } catch (e) {
        console.warn('Risk calculation for protocol', p.protocolId, e);
      }
    }
    const rpnValues = allRisks.map(r => r.rpn || 0);
    const maxRPN = rpnValues.length ? Math.max(...rpnValues) : 0;
    const avgRPN = rpnValues.length ? rpnValues.reduce((s, r) => s + r, 0) / rpnValues.length : 0;
    const criticalRisks = allRisks.filter(r => r.rpn >= 500);
    taskData.baseRPN = baseRPN;
    taskData.currentRPN = baseRPN;
    taskData.riskLevel = this.getRiskLevelFromRPN(baseRPN);
    taskData.risk_data = {
      max_rpn: maxRPN,
      avg_rpn: Math.round(avgRPN * 100) / 100,
      risk_count: allRisks.length,
      critical_risks: criticalRisks.map(r => ({ id: r.id, scenario: r.riskScenario, rpn: r.rpn, criticality: r.criticality })),
      biological_entities: Array.from(allBiologicalEntities).map(entityId => {
        for (const risk of allRisks) {
          if (risk.biological_entities && Array.isArray(risk.biological_entities)) {
            const entity = risk.biological_entities.find(e => (e.biologicalEntityId || e.id) === entityId);
            if (entity) return { id: entityId, name: entity.biologicalEntityName || entity.name || 'Unknown' };
          }
        }
        return { id: entityId, name: 'Unknown' };
      })
    };
    const docRef = await addDoc(this.tasksCollection, taskData);
    await this.logAction(docRef.id, userId, 'CREATE_TASK', null, null, { fromProtocolEntity: true, generatorLevel, protocolId: protocol.id });
    return docRef.id;
  }

  /**
   * Resolve protocol applicability rules to a list of execution locations (one per room or per department).
   * Returns array of { departmentId, departmentName, roomId?, roomName? }. If customContext already has a location, returns single-item list with that.
   */
  async _getExecutionLocationsFromApplicability(protocol, customContext) {
    if (customContext && (customContext.roomId || customContext.departmentId)) {
      return [{ ...customContext }];
    }
    const rules = protocol.applicabilityRules || [];
    if (rules.length === 0) return [{}];

    const locations = [];
    for (const r of rules) {
      if (r.structureType === 'rooms') {
        let roomList = [];
        if (r.includeAll) {
          roomList = await roomService.getAll();
        } else if (r.selectedItems && r.selectedItems.length) {
          const allRooms = await roomService.getAll();
          roomList = allRooms.filter(room => r.selectedItems.includes(room.id));
        }
        const allDepts = roomList.length ? await departmentService.getAll() : [];
        for (const room of roomList) {
          const dept = allDepts.find(d => d.id === room.departmentId);
          locations.push({
            roomId: room.id,
            roomName: room.name || room.id,
            departmentId: room.departmentId || null,
            departmentName: dept?.name || null
          });
        }
        if (locations.length) return locations;
      }
      if (r.structureType === 'departments') {
        let deptList = [];
        if (r.includeAll) {
          deptList = await departmentService.getAll();
        } else if (r.selectedItems && r.selectedItems.length) {
          const allDepts = await departmentService.getAll();
          deptList = allDepts.filter(d => r.selectedItems.includes(d.id));
        }
        for (const dept of deptList) {
          locations.push({
            departmentId: dept.id,
            departmentName: dept.name || dept.id,
            roomId: null,
            roomName: null
          });
        }
        if (locations.length) return locations;
      }
    }
    return [{}];
  }

  /**
   * Resolve Risk-Driven schedule for a task: get effective criticality for the execution location
   * and return the nomenclature default frequency (Phase 5).
   * @param {object} protocol - full protocol with schedulingRules
   * @param {object} customContext - must have organizationId and (departmentId or roomId); roomName/departmentName for display
   * @returns {Promise<{ frequencyCode, frequencyLabel, criticalityLevelId, criticalityLevelLabel, sourceLocationName, inherited } | null>}
   */
  async _resolveRiskDrivenSchedule(protocol, customContext) {
    const rules = protocol.schedulingRules || [];
    const riskDrivenRule = Array.isArray(rules) ? rules.find(r => r.frequency === SCHEDULE_TYPE_RISK_DRIVEN) : null;
    if (!riskDrivenRule) return null;

    const organizationId = customContext?.organizationId || protocol.organizationId;
    if (!organizationId) return null;

    let entityType, entityId, item;
    if (customContext?.roomId) {
      entityType = 'room';
      entityId = customContext.roomId;
    } else if (customContext?.departmentId) {
      entityType = 'department';
      entityId = customContext.departmentId;
    } else {
      return null;
    }

    try {
      const [departments, rooms] = await Promise.all([departmentService.getAll(), roomService.getAll()]);
      const structure = { departments: departments || [], rooms: rooms || [] };
      if (entityType === 'room') {
        item = (structure.rooms || []).find(r => r.id === entityId);
      } else {
        item = (structure.departments || []).find(d => d.id === entityId);
      }
      if (!item) return null;

      const effective = await getEffectiveCriticality(organizationId, entityType, entityId, structure, item);
      if (!effective?.level) return null;

      const frequencyCode = effective.level.defaultFrequencyCode || 'ONCE_DAILY';
      const frequencyLabel = RISK_DRIVEN_FREQUENCY_LABELS[frequencyCode] || frequencyCode.replace(/_/g, ' ');
      const sourceLocationName = customContext.roomName || customContext.departmentName || customContext.roomId || customContext.departmentId || 'Unknown location';

      return {
        frequencyCode,
        frequencyLabel,
        criticalityLevelId: effective.level.id,
        criticalityLevelLabel: effective.level.label || 'Unknown',
        sourceLocationName,
        inherited: !!effective.inherited
      };
    } catch (err) {
      console.warn('[TaskService] _resolveRiskDrivenSchedule failed:', err);
      return null;
    }
  }

  /**
   * Generate tasks from protocol. One task per location (applicability rules).
   * - Protocol-level only (checkbox at protocol, none at step/action): one task with full protocol (steps and actions).
   * - Step-level: one task per step that has "Generate task" (includes that step's actions); skip step if any action in that step generates a task.
   * - Action-level: one task per action that has "Generate task".
   * Protocol-, step- and action-level tasks can coexist when assignment differs (e.g. protocol → cleaning group, step → different group).
   */
  async generateTasksFromProtocol(protocolId, customContext, scheduledStart, userId, assignmentOverride = null) {
    try {
      const { protocolService } = await import('./protocolService');
      const protocol = await protocolService.getProtocol(protocolId);
      if (!protocol) throw new Error('Protocol not found');

      const locations = await this._getExecutionLocationsFromApplicability(protocol, customContext);
      const tasks = [];
      const fromWorkflow = !!(customContext && customContext.workflowExecutionId);

      for (const step of protocol.steps || []) {
        for (const action of step.actions || []) {
          if (action.generateTask) {
            for (const loc of locations) {
              const ctx = { ...customContext, ...loc };
              const taskId = await this.createTaskFromProtocolEntity(protocol, 'action', step, action, ctx, scheduledStart, userId, assignmentOverride);
              tasks.push({ taskId, level: 'action', protocolId: protocol.id, stepId: step.id || step.name, actionId: action.id || action.name });
            }
          }
        }
        const stepHasActionTasks = (step.actions || []).some(a => a.generateTask);
        if (!stepHasActionTasks && step.generateTask) {
          for (const loc of locations) {
            const ctx = { ...customContext, ...loc };
            const taskId = await this.createTaskFromProtocolEntity(protocol, 'step', step, null, ctx, scheduledStart, userId, assignmentOverride);
            tasks.push({ taskId, level: 'step', protocolId: protocol.id, stepId: step.id || step.name });
          }
        }
      }

      const protocolGeneratesTask = protocol.generateTaskAtProtocolLevel === true || (protocol.defaultTaskTemplateId && tasks.length === 0);
      if (protocolGeneratesTask) {
        for (const loc of locations) {
          const ctx = { ...customContext, ...loc };
          const taskId = await this.createTaskFromProtocolEntity(protocol, 'protocol', null, null, ctx, scheduledStart, userId, assignmentOverride);
          tasks.push({ taskId, level: 'protocol', protocolId: protocol.id });
        }
      }

      if (tasks.length === 0 && fromWorkflow) {
        for (const loc of locations) {
          const ctx = { ...customContext, ...loc };
          const taskId = await this.createTaskFromProtocolEntity(protocol, 'protocol', null, null, ctx, scheduledStart, userId, assignmentOverride);
          tasks.push({ taskId, level: 'protocol', protocolId: protocol.id });
        }
        if (tasks.length > 0) {
          const protoRules = (Array.isArray(protocol.assignmentRules) ? protocol.assignmentRules[0] : protocol.assignmentRules) || {};
          console.log(`✅ Created default protocol-level task for workflow: ${protocol.name} (${tasks.length} per location)`, protoRules.assignedToGroup ? `(assigned to group: ${protoRules.assignedToGroup})` : '');
        }
      }

      console.log(`✅ Generated ${tasks.length} tasks from protocol: ${protocol.name} (${locations.length} location(s))`);
      return tasks;
    } catch (error) {
      console.error('Error generating tasks from protocol:', error);
      throw error;
    }
  }
  
  // ==================== TASK EXECUTION ====================
  
  /**
   * Start task execution
   */
  async startTask(taskId, userId) {
    try {
      const task = await this.getTask(taskId);
      if (!task) throw new Error('Task not found');
      
      // Validate user can start: directly assigned, group-assigned, or unassigned (anyone can claim)
      const isDirectlyAssigned = task.assignedTo === userId;
      const isGroupTask = task.assignedToGroup && !task.assignedTo;
      const isUnassigned = !task.assignedTo && !task.assignedToGroup;
      if (!isDirectlyAssigned && !isGroupTask && !isUnassigned) {
        throw new Error('User not authorized to start this task');
      }
      
      if (task.status !== 'assigned' && task.status !== 'acknowledged') {
        throw new Error(`Cannot start task with status: ${task.status}`);
      }
      
      const now = Timestamp.now();
      const updates = {
        status: 'in_progress',
        actualStart: now,
        updatedAt: now,
        updatedBy: userId
      };
      // Claim task for this user when starting (group or unassigned); mark as assigned to that user
      if (!task.assignedTo && (task.assignedToGroup || isUnassigned)) {
        updates.assignedTo = userId;
        const userName = await this._getUserDisplayName(userId);
        if (userName) updates.assignedToName = userName;
      }
      await updateDoc(doc(this.tasksCollection, taskId), removeUndefined(updates));
      
      // Log the start
      await this.logAction(taskId, userId, 'START_TASK', null, null, {
        scheduledStart: task.scheduledStart,
        actualStart: now
      });
      
      console.log(`✅ Started task: ${taskId}`);
    } catch (error) {
      if (error?.code !== 'permission-denied') {
        console.error('Error starting task:', error);
      }
      throw error;
    }
  }
  
  /**
   * Start protocol execution within task
   */
  async startProtocol(taskId, protocolIndex, userId) {
    try {
      const task = await this.getTask(taskId);
      if (!task) throw new Error('Task not found');
      
      const canExecute = task.assignedTo === userId || (task.assignedToGroup && !task.assignedTo) || (!task.assignedTo && !task.assignedToGroup);
      if (!canExecute) {
        throw new Error('User not authorized to execute this task');
      }
      
      const protocol = task.protocols[protocolIndex];
      if (!protocol) throw new Error('Protocol not found in task');
      
      const now = Timestamp.now();
      
      // Update protocol status
      const updatedProtocols = [...task.protocols];
      updatedProtocols[protocolIndex] = {
        ...protocol,
        status: 'in_progress',
        startTime: now
      };
      
      // Update task
      await updateDoc(doc(this.tasksCollection, taskId), {
        protocols: updatedProtocols,
        currentProtocolIndex: protocolIndex,
        currentStepIndex: 0,
        updatedAt: now,
        updatedBy: userId
      });
      
      // Log protocol start
      await this.logAction(taskId, userId, 'START_PROTOCOL', protocolIndex, null, {
        protocolId: protocol.protocolId,
        protocolName: protocol.protocolName
      });
      
      console.log(`✅ Started protocol ${protocolIndex} in task: ${taskId}`);
    } catch (error) {
      console.error('Error starting protocol:', error);
      throw error;
    }
  }
  
  /**
   * Set a single action's completed state within a step (for step-as-task-generator flows).
   */
  async setStepActionCompleted(taskId, protocolIndex, stepIndex, actionIndex, completed, userId) {
    try {
      const task = await this.getTask(taskId);
      if (!task) throw new Error('Task not found');

      const canExecute = task.assignedTo === userId || (task.assignedToGroup && !task.assignedTo) || (!task.assignedTo && !task.assignedToGroup);
      if (!canExecute) {
        throw new Error('User not authorized to execute this task');
      }

      const protocol = task.protocols[protocolIndex];
      if (!protocol) throw new Error('Protocol not found');
      const step = protocol.steps[stepIndex];
      if (!step) throw new Error('Step not found');
      const actions = step.actions || [];
      if (actionIndex < 0 || actionIndex >= actions.length) throw new Error('Action not found');

      const updatedProtocols = task.protocols.map((p, pi) => {
        if (pi !== protocolIndex) return p;
        return {
          ...p,
          steps: (p.steps || []).map((s, si) => {
            if (si !== stepIndex) return s;
            const updatedActions = (s.actions || []).map((a, ai) =>
              ai === actionIndex ? { ...a, completed: !!completed } : a
            );
            // Step is complete only when all actions are checked; mark incomplete for risk recalculation when any action is unchecked
            const allActionsComplete = updatedActions.length > 0 && updatedActions.every(a => a.completed === true);
            return {
              ...s,
              actions: updatedActions,
              isComplete: allActionsComplete
            };
          })
        };
      });

      const now = Timestamp.now();
      await updateDoc(doc(this.tasksCollection, taskId), {
        protocols: updatedProtocols,
        updatedAt: now,
        updatedBy: userId
      });

      console.log(`✅ Action ${actionIndex} in step ${stepIndex} marked ${completed ? 'complete' : 'incomplete'}`);
    } catch (error) {
      console.error('Error setting step action completed:', error);
      throw error;
    }
  }

  /**
   * Complete step within protocol
   */
  async completeStep(taskId, protocolIndex, stepIndex, userId, stepData = {}) {
    try {
      const task = await this.getTask(taskId);
      if (!task) throw new Error('Task not found');
      
      const canExecute = task.assignedTo === userId || (task.assignedToGroup && !task.assignedTo) || (!task.assignedTo && !task.assignedToGroup);
      if (!canExecute) {
        throw new Error('User not authorized to execute this task');
      }
      
      const protocol = task.protocols[protocolIndex];
      if (!protocol) throw new Error('Protocol not found');
      
      const step = protocol.steps[stepIndex];
      if (!step) throw new Error('Step not found');
      
      const now = Timestamp.now();
      
      // Calculate actual duration
      const startTime = step.startTime || protocol.startTime || task.actualStart;
      const actualDuration = startTime 
        ? Math.floor((now.toMillis() - startTime.toMillis()) / 1000)
        : 0;
      
      const isOnTime = actualDuration <= step.expectedDuration;
      const actions = step.actions || [];
      const allActionsDone = actions.length === 0 || actions.every(a => a.completed === true);
      const completedWithErrors = !allActionsDone;
      const incompleteActionNames = completedWithErrors && actions.length > 0
        ? actions.filter(a => !a.completed).map(a => a.actionName || a.name || 'Action').filter(Boolean)
        : undefined;

      const updatedProtocols = [...task.protocols];
      updatedProtocols[protocolIndex].steps[stepIndex] = {
        ...step,
        status: completedWithErrors ? 'completed_with_errors' : 'completed',
        endTime: now,
        duration: actualDuration,
        isOnTime,
        isComplete: true,
        completedWithErrors: completedWithErrors || undefined,
        incompleteActionNames: incompleteActionNames || undefined,
        notes: stepData.notes || null,
        photos: stepData.photos || [],
        documentation: stepData.documentation || null
      };

      // Check if all steps in protocol are complete or skipped
      const allStepsComplete = updatedProtocols[protocolIndex].steps.every(s =>
        s.isComplete || s.isSkipped
      );

      if (allStepsComplete) {
        const protocolSteps = updatedProtocols[protocolIndex].steps;
        const hasSkipsOrErrors = protocolSteps.some(s => s.isSkipped || s.completedWithErrors);
        updatedProtocols[protocolIndex].status = hasSkipsOrErrors ? 'completed_with_skips' : 'completed';
        updatedProtocols[protocolIndex].endTime = now;
        updatedProtocols[protocolIndex].completedWithSkips = !!hasSkipsOrErrors;

        const protocolStartTime = updatedProtocols[protocolIndex].startTime;
        if (protocolStartTime) {
          updatedProtocols[protocolIndex].duration = Math.floor(
            (now.toMillis() - protocolStartTime.toMillis()) / 1000
          );
        }

        // Compliance: skipped = 0, completed_with_errors = 0.6, fully completed = 1; then factor on-time for completed
        const totalSteps = protocolSteps.length;
        let scoreSum = 0;
        protocolSteps.forEach(s => {
          if (s.isSkipped) scoreSum += 0;
          else if (s.completedWithErrors) scoreSum += 0.6;
          else scoreSum += (s.isOnTime ? 1 : 0.85);
        });
        updatedProtocols[protocolIndex].complianceScore = totalSteps === 0 ? 0 : Math.round((scoreSum / totalSteps) * 100);
      }
      
      // Update task
      await updateDoc(doc(this.tasksCollection, taskId), {
        protocols: updatedProtocols,
        currentStepIndex: stepIndex + 1,
        updatedAt: now,
        updatedBy: userId
      });
      
      // Log step completion
      await this.logAction(taskId, userId, 'COMPLETE_STEP', protocolIndex, stepIndex, {
        duration: actualDuration,
        expectedDuration: step.expectedDuration,
        isOnTime
      });
      
      console.log(`✅ Completed step ${stepIndex} in protocol ${protocolIndex}`);
    } catch (error) {
      console.error('Error completing step:', error);
      throw error;
    }
  }
  
  /**
   * Skip step within protocol
   */
  async skipStep(taskId, protocolIndex, stepIndex, userId, skipReason) {
    try {
      const task = await this.getTask(taskId);
      if (!task) throw new Error('Task not found');
      
      const canExecute = task.assignedTo === userId || (task.assignedToGroup && !task.assignedTo) || (!task.assignedTo && !task.assignedToGroup);
      if (!canExecute) {
        throw new Error('User not authorized to execute this task');
      }
      
      const protocol = task.protocols[protocolIndex];
      if (!protocol) throw new Error('Protocol not found');
      
      const step = protocol.steps[stepIndex];
      if (!step) throw new Error('Step not found');
      
      const now = Timestamp.now();
      
      // Update step as skipped
      const updatedProtocols = [...task.protocols];
      updatedProtocols[protocolIndex].steps[stepIndex] = {
        ...step,
        status: 'skipped',
        isSkipped: true,
        skipReason,
        endTime: now
      };

      const protocolSteps = updatedProtocols[protocolIndex].steps;
      const allStepsFinished = protocolSteps.every(s => s.isComplete || s.isSkipped);
      if (allStepsFinished) {
        updatedProtocols[protocolIndex].status = 'completed_with_skips';
        updatedProtocols[protocolIndex].endTime = now;
        updatedProtocols[protocolIndex].completedWithSkips = true;
        if (protocol.startTime) {
          updatedProtocols[protocolIndex].duration = Math.floor(
            (now.toMillis() - protocol.startTime.toMillis()) / 1000
          );
        }
        let scoreSum = 0;
        protocolSteps.forEach(s => {
          if (s.isSkipped) scoreSum += 0;
          else if (s.completedWithErrors) scoreSum += 0.6;
          else scoreSum += (s.isOnTime ? 1 : 0.85);
        });
        updatedProtocols[protocolIndex].complianceScore = protocolSteps.length === 0 ? 0 : Math.round((scoreSum / protocolSteps.length) * 100);
      }

      // Record deviation
      const deviation = {
        timestamp: now,
        type: 'step_skipped',
        description: `Step "${step.stepName}" in protocol "${protocol.protocolName}" was skipped: ${skipReason}`,
        impact: step.isCritical ? 'High' : 'Medium',
        resolved: false
      };

      await updateDoc(doc(this.tasksCollection, taskId), {
        protocols: updatedProtocols,
        currentStepIndex: stepIndex + 1,
        deviations: [...task.deviations, deviation],
        updatedAt: now,
        updatedBy: userId
      });
      
      // Log skip
      await this.logAction(taskId, userId, 'SKIP_STEP', protocolIndex, stepIndex, {
        skipReason,
        isCritical: step.isCritical
      });
      
      console.log(`⚠️ Skipped step ${stepIndex} in protocol ${protocolIndex}`);
    } catch (error) {
      console.error('Error skipping step:', error);
      throw error;
    }
  }
  
  /**
   * Complete protocol within task
   */
  async completeProtocol(taskId, protocolIndex, userId, protocolData = {}) {
    try {
      const task = await this.getTask(taskId);
      if (!task) throw new Error('Task not found');
      
      const protocol = task.protocols[protocolIndex];
      if (!protocol) throw new Error('Protocol not found');
      
      // Verify all steps are complete or skipped
      const allStepsFinished = protocol.steps.every(s => s.isComplete || s.isSkipped);
      if (!allStepsFinished) {
        throw new Error('Cannot complete protocol - not all steps are finished');
      }
      
      const now = Timestamp.now();
      const protocolSteps = protocol.steps || [];
      const hasSkipsOrErrors = protocolSteps.some(s => s.isSkipped || s.completedWithErrors);
      const protocolStatus = hasSkipsOrErrors ? 'completed_with_skips' : 'completed';

      const updatedProtocols = [...task.protocols];
      updatedProtocols[protocolIndex] = {
        ...protocol,
        status: protocolStatus,
        endTime: now,
        completedWithSkips: !!hasSkipsOrErrors
      };

      if (protocol.startTime) {
        updatedProtocols[protocolIndex].duration = Math.floor(
          (now.toMillis() - protocol.startTime.toMillis()) / 1000
        );
      }
      if (protocol.complianceScore == null && protocolSteps.length > 0) {
        let scoreSum = 0;
        protocolSteps.forEach(s => {
          if (s.isSkipped) scoreSum += 0;
          else if (s.completedWithErrors) scoreSum += 0.6;
          else scoreSum += (s.isOnTime ? 1 : 0.85);
        });
        updatedProtocols[protocolIndex].complianceScore = Math.round((scoreSum / protocolSteps.length) * 100);
      }

      await updateDoc(doc(this.tasksCollection, taskId), {
        protocols: updatedProtocols,
        currentProtocolIndex: protocolIndex + 1,
        updatedAt: now,
        updatedBy: userId
      });

      await this.logAction(taskId, userId, 'COMPLETE_PROTOCOL', protocolIndex, null, {
        protocolId: protocol.protocolId,
        duration: updatedProtocols[protocolIndex].duration,
        complianceScore: updatedProtocols[protocolIndex].complianceScore,
        completedWithSkips: hasSkipsOrErrors
      });

      // Emit compliance event so QMS pipeline can aggregate real execution data
      this._emitProtocolComplianceEvent(task, updatedProtocols[protocolIndex], userId)
        .catch(err => console.warn('Compliance event emit failed (non-blocking):', err));
      
      console.log(`✅ Completed protocol ${protocolIndex} in task: ${taskId}`);
    } catch (error) {
      console.error('Error completing protocol:', error);
      throw error;
    }
  }

  /**
   * Emit a protocol_compliance_events document after a protocol finishes.
   * Fire-and-forget — does not throw.
   */
  async _emitProtocolComplianceEvent(task, completedProtocol, userId) {
    try {
      const { complianceEventService } = await import('./complianceEventService');

      const score = completedProtocol.complianceScore ?? 0;
      const complianceStatus =
        score >= 80 ? 'compliant' : score > 0 ? 'partial_compliant' : 'non_compliant';

      const steps = completedProtocol.steps || [];
      const completedSteps = steps.filter(s => s.isComplete && !s.isSkipped).length;
      const totalSteps = steps.length;

      // Map protocol category to a known event type, fallback to 'environmental_cleaning'
      const eventTypeMap = {
        hand_hygiene: 'hand_hygiene',
        ppe: 'ppe_usage',
        isolation: 'isolation_protocol',
        stewardship: 'antimicrobial_stewardship',
        cleaning: 'environmental_cleaning',
        disinfection: 'environmental_cleaning',
      };
      const rawCategory = (completedProtocol.category || '').toLowerCase();
      const eventType =
        Object.entries(eventTypeMap).find(([key]) => rawCategory.includes(key))?.[1] ||
        'environmental_cleaning';

      await complianceEventService.createEvent(
        {
          eventType,
          eventSubType: 'task_execution',
          location: {
            facilityId: task.facilityId || task.context?.facilityId || 'facility_001',
            floorId: task.context?.floorId || null,
            departmentId: task.context?.departmentId || task.departmentId || null,
            roomId: task.context?.roomId || null,
            bedId: task.context?.bedId || null,
          },
          personnel: {
            userId,
            role: task.assignedToRole || task.context?.role || 'staff',
            departmentId: task.context?.departmentId || task.departmentId || null,
            shiftId: null,
          },
          protocol: {
            protocolId: completedProtocol.protocolId,
            protocolName: completedProtocol.protocolName || completedProtocol.protocolId,
            requiredActions: steps.map(s => s.stepName || s.name).filter(Boolean),
            complianceStatus,
            complianceScore: score / 100,
          },
          eventDetails: {
            duration: completedProtocol.duration || 0,
            method: 'manual_entry',
            notes: '',
            equipmentUsed: [],
            patientContact: !!(task.context?.patientId || task.patientId),
          },
          complianceMetrics: {
            actionsCompleted: completedSteps,
            actionsRequired: totalSteps,
            timeToComplete: completedProtocol.duration || 0,
            qualityScore: score / 100,
            deviations: [],
          },
        },
        userId
      );
    } catch (err) {
      console.warn('[TaskService] _emitProtocolComplianceEvent:', err?.message || err);
    }
  }
  
  /**
   * Complete entire task
   */
  async completeTask(taskId, userId, taskData = {}) {
    try {
      const task = await this.getTask(taskId);
      if (!task) throw new Error('Task not found');
      
      const canComplete = task.assignedTo === userId || (task.assignedToGroup && !task.assignedTo) || (!task.assignedTo && !task.assignedToGroup);
      if (!canComplete) {
        throw new Error('User not authorized to complete this task');
      }

      const protocols = Array.isArray(task.protocols) ? task.protocols : [];

      const allProtocolsFinished =
        protocols.length === 0 ||
        protocols.every(
          (p) => p.status === 'completed' || p.status === 'completed_with_skips' || p.status === 'skipped'
        );
      
      if (!allProtocolsFinished) {
        throw new Error('Cannot complete task - not all protocols are finished');
      }
      
      const now = Timestamp.now();

      const hasAnySkipsOrErrors = protocols.some(p =>
        p.status === 'completed_with_skips' || p.completedWithSkips ||
        (p.steps || []).some(s => s.isSkipped || s.completedWithErrors)
      );

      const protocolScores = protocols
        .filter(p => p.complianceScore !== null && p.complianceScore !== undefined)
        .map(p => p.complianceScore);

      const overallCompliance = protocolScores.length > 0
        ? Math.round(protocolScores.reduce((sum, score) => sum + score, 0) / protocolScores.length)
        : (hasAnySkipsOrErrors ? 0 : 100);

      const completeUpdates = removeUndefined({
        status: 'completed',
        actualEnd: now,
        complianceScore: overallCompliance,
        ...(hasAnySkipsOrErrors && { completedWithSkips: true }),
        completionQuality: taskData.completionQuality || 'good',
        notes: taskData.notes || task.notes || '',
        updatedAt: now,
        updatedBy: userId
      });
      await updateDoc(doc(this.tasksCollection, taskId), completeUpdates);
      
      // Log task completion
      await this.logAction(taskId, userId, 'COMPLETE_TASK', null, null, {
        complianceScore: overallCompliance,
        completionQuality: taskData.completionQuality,
        totalProtocols: protocols.length,
        completedProtocols: protocols.filter(p => p.status === 'completed' || p.status === 'completed_with_skips').length
      });
      try {
        const taskName = protocols.map(p => p.protocolName).filter(Boolean).join(', ') || task.name || taskId;
        auditService.logUpdate('task_instances', taskId, taskName, { action: 'complete', complianceScore: overallCompliance });
      } catch (e) {
        console.warn('Audit log failed:', e);
      }

      // Phase 2.1: Evaluate thresholds (async, fire-and-forget); creates system incidents on breach
      const { thresholdBreachService } = await import('./thresholdBreachService');
      thresholdBreachService.evaluateThresholdsAtTaskCompletion({
        executionId: null,
        task,
        complianceScore: overallCompliance,
        userId
      }).catch(err => console.warn('Threshold evaluation:', err));

      // Resume workflow execution if this task was spawned by a workflow node
      const workflowExecId = task.triggeredByWorkflow || task.context?.workflowExecutionId;
      if (workflowExecId) {
        import('./workflowExecutionService').then(({ checkAndResumeWorkflowExecution }) => {
          checkAndResumeWorkflowExecution(workflowExecId, userId)
            .catch(err => console.warn('Workflow resume after task completion failed:', err));
        });
      }

      // ETF: populează execution_trust layers dacă task-ul e dintr-un workflow
      if (workflowExecId) {
        import('./executionTrustService').then(async ({ populateExecutionTrustLayers }) => {
          try {
            const completedTaskDoc = await this.getTask(taskId);
            const executorName = await this._getUserDisplayName(userId);
            await populateExecutionTrustLayers(workflowExecId, completedTaskDoc, executorName);
          } catch (err) {
            console.warn('ETF population failed (non-blocking):', err);
          }
        });
      }

      if (task.triggeredBy === 'recommendation' && task.source_id) {
        import('./recommendationService')
          .then(({ recommendationService }) => {
            recommendationService
              .markImplemented(task.source_id, userId)
              .catch((e) => console.warn('[TaskService] markImplemented recommendation failed (non-blocking):', e));
          })
          .catch((e) => console.warn('[TaskService] recommendationService import failed:', e));
      }

      console.log(`✅ Completed task: ${taskId}`);
    } catch (error) {
      console.error('Error completing task:', error);
      throw error;
    }
  }
  
  // ==================== TASK MANAGEMENT ====================
  
  /**
   * Reassign task to different user
   */
  async reassignTask(taskId, newUserId, newUserName, reason, userId) {
    try {
      const task = await this.getTask(taskId);
      if (!task) throw new Error('Task not found');
      
      const now = Timestamp.now();
      
      await updateDoc(doc(this.tasksCollection, taskId), {
        assignedTo: newUserId,
        assignedToName: newUserName,
        assignedAt: now,
        acknowledgedAt: null,
        updatedAt: now,
        updatedBy: userId
      });
      
      await this.logAction(taskId, userId, 'REASSIGN_TASK', null, null, {
        previousAssignee: task.assignedTo,
        newAssignee: newUserId,
        reason
      });
      
      console.log(`✅ Reassigned task ${taskId} to ${newUserId}`);
    } catch (error) {
      console.error('Error reassigning task:', error);
      throw error;
    }
  }
  
  /**
   * Cancel task
   */
  async cancelTask(taskId, reason, userId) {
    try {
      const task = await this.getTask(taskId);
      if (!task) throw new Error('Task not found');
      
      const now = Timestamp.now();
      
      await updateDoc(doc(this.tasksCollection, taskId), {
        status: 'cancelled',
        notes: `${task.notes || ''}\n\nCancelled: ${reason}`,
        updatedAt: now,
        updatedBy: userId
      });
      
      await this.logAction(taskId, userId, 'CANCEL_TASK', null, null, { reason });
      
      console.log(`✅ Cancelled task: ${taskId}`);
    } catch (error) {
      console.error('Error cancelling task:', error);
      throw error;
    }
  }
  
  /**
   * Update task context
   */
  async updateTaskContext(taskId, contextUpdates, userId) {
    try {
      const task = await this.getTask(taskId);
      if (!task) throw new Error('Task not found');
      
      const updatedContext = {
        ...task.context,
        ...contextUpdates
      };
      
      await updateDoc(doc(this.tasksCollection, taskId), {
        context: updatedContext,
        updatedAt: Timestamp.now(),
        updatedBy: userId
      });
      
      await this.logAction(taskId, userId, 'UPDATE_CONTEXT', null, null, { contextUpdates });
      
      console.log(`✅ Updated context for task: ${taskId}`);
    } catch (error) {
      console.error('Error updating task context:', error);
      throw error;
    }
  }
  
  /**
   * Update task priority
   */
  async updateTaskPriority(taskId, priority, urgency, userId) {
    try {
      const updates = {
        updatedAt: Timestamp.now(),
        updatedBy: userId
      };
      
      if (priority) updates.priority = priority;
      if (urgency) updates.urgency = urgency;
      
      await updateDoc(doc(this.tasksCollection, taskId), removeUndefined(updates));
      
      await this.logAction(taskId, userId, 'UPDATE_PRIORITY', null, null, { priority, urgency });
      
      console.log(`✅ Updated priority for task: ${taskId}`);
    } catch (error) {
      console.error('Error updating task priority:', error);
      throw error;
    }
  }
  
  /**
   * Acknowledge task (executor confirms receipt)
   */
  async acknowledgeTask(taskId, userId) {
    try {
      const task = await this.getTask(taskId);
      if (!task) throw new Error('Task not found');
      
      const canAcknowledge = task.assignedTo === userId || (task.assignedToGroup && !task.assignedTo) || (!task.assignedTo && !task.assignedToGroup);
      if (!canAcknowledge) {
        throw new Error('User not authorized to acknowledge this task');
      }
      
      const now = Timestamp.now();
      const ackUpdates = {
        status: 'acknowledged',
        acknowledgedAt: now,
        updatedAt: now,
        updatedBy: userId
      };
      if (!task.assignedTo && (task.assignedToGroup || !task.assignedToGroup)) {
        ackUpdates.assignedTo = userId;
        const userName = await this._getUserDisplayName(userId);
        if (userName) ackUpdates.assignedToName = userName;
      }
      await updateDoc(doc(this.tasksCollection, taskId), removeUndefined(ackUpdates));
      
      await this.logAction(taskId, userId, 'ACKNOWLEDGE_TASK', null, null, {});
      
      console.log(`✅ Acknowledged task: ${taskId}`);
    } catch (error) {
      console.error('Error acknowledging task:', error);
      throw error;
    }
  }
  
  // ==================== QUERY METHODS ====================

  /**
   * Resolve display name for a user (for task assignment when user claims by start/acknowledge).
   */
  async _getUserDisplayName(userId) {
    if (!userId) return null;
    try {
      const userSnap = await getDoc(doc(db, 'users', userId));
      if (!userSnap.exists()) return null;
      const d = userSnap.data();
      // Prefer name over email for display
      return d.full_name || d.displayName || d.display_name || d.name || d.email || null;
    } catch {
      return null;
    }
  }
  
  /**
   * Get task by ID
   */
  async getTask(taskId) {
    try {
      const docRef = doc(this.tasksCollection, taskId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      if (error?.code !== 'permission-denied') {
        console.error('Error fetching task:', error);
      }
      throw error;
    }
  }
  
  /**
   * Fetch tasks directly assigned to user. Returns array of doc snapshots.
   */
  async _fetchDirectTasksForUser(userId) {
    try {
      const q = query(
        this.tasksCollection,
        where('assignedTo', '==', userId),
        orderBy('scheduledStart', 'desc')
      );
      const snap = await getDocs(q);
      return snap.docs;
    } catch (e) {
      console.warn('getTasksByUser: direct query failed, trying without orderBy:', e.message);
      try {
        const q = query(this.tasksCollection, where('assignedTo', '==', userId));
        const snap = await getDocs(q);
        return snap.docs;
      } catch (e2) {
        console.error('getTasksByUser: direct fallback failed:', e2);
        return [];
      }
    }
  }

  /**
   * Resolve to fallback after ms if promise doesn't resolve (avoids hanging on slow/hanging calls).
   */
  _withTimeout(promise, ms, fallback) {
    return Promise.race([
      promise,
      new Promise(resolve => setTimeout(() => resolve(fallback), ms))
    ]);
  }

  /**
   * Fetch tasks assigned to groups the user belongs to. Returns array of doc snapshots.
   * getGroupIdsForUser is capped at 6s so a slow/hanging user_groups query doesn't block the whole load.
   */
  async _fetchGroupTasksForUser(userId) {
    let groupIds = [];
    try {
      groupIds = await this._withTimeout(
        userGroupService.getGroupIdsForUser(userId),
        6000,
        []
      );
      if (groupIds.length === 0 && !Array.isArray(groupIds)) groupIds = [];
    } catch (e) {
      console.warn('Could not load user groups for task list:', e);
      return [];
    }
    if (groupIds.length === 0) return [];
    const IN_LIMIT = 10;
    const chunkPromises = [];
    for (let i = 0; i < groupIds.length; i += IN_LIMIT) {
      const chunk = groupIds.slice(i, i + IN_LIMIT);
      chunkPromises.push(
        (async () => {
          try {
            const q = query(
              this.tasksCollection,
              where('assignedToGroup', 'in', chunk),
              orderBy('scheduledStart', 'desc')
            );
            const snap = await getDocs(q);
            return snap.docs;
          } catch (e) {
            try {
              const q = query(this.tasksCollection, where('assignedToGroup', 'in', chunk));
              const snap = await getDocs(q);
              return snap.docs;
            } catch (e2) {
              return [];
            }
          }
        })()
      );
    }
    const chunkResults = await Promise.all(chunkPromises);
    return chunkResults.flat();
  }

  /** Max unassigned tasks to load so the task list doesn't hang on huge collections. */
  static UNASSIGNED_TASKS_LIMIT = 150;

  /**
   * Fetch unassigned workflow tasks (doc snapshots). Caller filters by workflow + active.
   * Limited to avoid loading thousands of docs when the pool is large.
   */
  async _fetchUnassignedWorkflowTasks() {
    try {
      const q = query(
        this.tasksCollection,
        where('assignedTo', '==', null),
        orderBy('scheduledStart', 'desc'),
        limit(TaskService.UNASSIGNED_TASKS_LIMIT)
      );
      const snap = await getDocs(q);
      return snap.docs;
    } catch (e) {
      try {
        const q = query(
          this.tasksCollection,
          where('assignedTo', '==', null),
          limit(TaskService.UNASSIGNED_TASKS_LIMIT)
        );
        const snap = await getDocs(q);
        return snap.docs;
      } catch (e2) {
        return [];
      }
    }
  }

  /**
   * Get tasks assigned to a user (directly or via a group the user belongs to).
   * Runs direct, group, and unassigned queries in parallel to avoid timeout.
   */
  async getTasksByUser(userId, filters = {}) {
    try {
      const taskMap = new Map(); // id -> task for deduplication

      const addToMap = (docs) => {
        docs.forEach(d => {
          const task = { id: d.id, ...d.data() };
          taskMap.set(d.id, task);
        });
      };

      // Run all three sources in parallel; each capped at 12s so one hanging query doesn't block the rest
      const FETCH_TIMEOUT_MS = 12000;
      const [directTasks, groupTasks, unassignedTasks] = await Promise.all([
        this._withTimeout(this._fetchDirectTasksForUser(userId), FETCH_TIMEOUT_MS, []),
        this._withTimeout(this._fetchGroupTasksForUser(userId), FETCH_TIMEOUT_MS, []),
        this._withTimeout(this._fetchUnassignedWorkflowTasks(), FETCH_TIMEOUT_MS, [])
      ]);

      addToMap(directTasks);
      addToMap(groupTasks);
      // Include unassigned tasks that are active (any source: workflow, scheduler, manual) so they appear in My Tasks for claiming
      unassignedTasks.forEach(d => {
        const task = { id: d.id, ...d.data() };
        const active = task.status && !['completed', 'cancelled'].includes(task.status);
        if (active) {
          taskMap.set(d.id, task);
        }
      });

      let tasks = Array.from(taskMap.values());

      // Sort by creation time descending so newest (most recently generated) tasks appear at the top
      tasks.sort((a, b) => {
        const aCreated = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.getTime?.() ?? 0);
        const bCreated = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.getTime?.() ?? 0);
        if (bCreated !== aCreated) return bCreated - aCreated;
        const aSched = a.scheduledStart?.toMillis ? a.scheduledStart.toMillis() : (a.scheduledStart?.getTime?.() ?? 0);
        const bSched = b.scheduledStart?.toMillis ? b.scheduledStart.toMillis() : (b.scheduledStart?.getTime?.() ?? 0);
        return bSched - aSched;
      });

      // Apply client-side filters
      if (filters.status) {
        tasks = tasks.filter(t => t.status === filters.status);
      }
      if (filters.priority) {
        tasks = tasks.filter(t => t.priority === filters.priority);
      }
      if (filters.taskType) {
        tasks = tasks.filter(t => t.taskType === filters.taskType);
      }
      if (filters.startDate) {
        const startDate = filters.startDate.getTime ? filters.startDate.getTime() : filters.startDate;
        tasks = tasks.filter(t => {
          const taskDate = t.scheduledStart.toMillis ? t.scheduledStart.toMillis() : t.scheduledStart;
          return taskDate >= startDate;
        });
      }
      if (filters.endDate) {
        const endDate = filters.endDate.getTime ? filters.endDate.getTime() : filters.endDate;
        tasks = tasks.filter(t => {
          const taskDate = t.scheduledStart.toMillis ? t.scheduledStart.toMillis() : t.scheduledStart;
          return taskDate <= endDate;
        });
      }
      if (filters.departmentId) {
        tasks = tasks.filter(t => (t.context && t.context.departmentId) === filters.departmentId);
      }
      if (filters.roomId) {
        tasks = tasks.filter(t => (t.context && t.context.roomId) === filters.roomId);
      }

      return tasks;
    } catch (error) {
      console.error('Error fetching tasks by user:', error);
      throw error;
    }
  }
  
  /**
   * Get overdue tasks
   */
  async getOverdueTasks(filters = {}) {
    try {
      const now = Timestamp.now();
      
      const q = query(
        this.tasksCollection,
        where('status', 'in', ['assigned', 'acknowledged', 'in_progress']),
        where('scheduledEnd', '<', now),
        orderBy('scheduledEnd', 'asc')
      );
      
      const snapshot = await getDocs(q);
      let tasks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply additional filters
      if (filters.departmentId) {
        tasks = tasks.filter(t => t.context.departmentId === filters.departmentId);
      }
      
      return tasks;
    } catch (error) {
      console.error('Error fetching overdue tasks:', error);
      throw error;
    }
  }
  
  /**
   * Get tasks by status
   */
  async getTasksByStatus(status, filters = {}) {
    try {
      let q = query(
        this.tasksCollection,
        where('status', '==', status),
        orderBy('scheduledStart', 'desc')
      );
      
      const snapshot = await getDocs(q);
      let tasks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply additional filters
      if (filters.departmentId) {
        tasks = tasks.filter(t => t.context.departmentId === filters.departmentId);
      }
      
      if (filters.taskType) {
        tasks = tasks.filter(t => t.taskType === filters.taskType);
      }
      
      return tasks;
    } catch (error) {
      console.error('Error fetching tasks by status:', error);
      throw error;
    }
  }
  
  /**
   * Get tasks by template
   */
  async getTasksByTemplate(templateId, filters = {}) {
    try {
      let q = query(
        this.tasksCollection,
        where('templateId', '==', templateId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      let tasks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply status filter
      if (filters.status) {
        tasks = tasks.filter(t => t.status === filters.status);
      }
      
      return tasks;
    } catch (error) {
      console.error('Error fetching tasks by template:', error);
      throw error;
    }
  }
  
  /**
   * Get tasks by package ID
   */
  async getTasksByPackage(packageId) {
    try {
      const q = query(
        this.tasksCollection,
        where('packageId', '==', packageId),
        orderBy('sequenceOrder', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching tasks by package:', error);
      throw error;
    }
  }
  
  /**
   * Get all tasks with filters. Sorted by scheduledStart descending (most recent first).
   */
  async getAllTasks(filters = {}) {
    try {
      let q = query(this.tasksCollection, orderBy('scheduledStart', 'desc'));
      
      const snapshot = await getDocs(q);
      let tasks = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id
      }));
      
      // Apply client-side filters
      if (filters.status) {
        tasks = tasks.filter(t => t.status === filters.status);
      }
      
      if (filters.priority) {
        tasks = tasks.filter(t => t.priority === filters.priority);
      }
      
      if (filters.taskType) {
        tasks = tasks.filter(t => t.taskType === filters.taskType);
      }
      
      if (filters.departmentId) {
        tasks = tasks.filter(t => t.context && t.context.departmentId === filters.departmentId);
      }
      if (filters.roomId) {
        tasks = tasks.filter(t => t.context && t.context.roomId === filters.roomId);
      }
      
      if (filters.assignedTo) {
        tasks = tasks.filter(t => t.assignedTo === filters.assignedTo);
      }
      
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        tasks = tasks.filter(t => 
          t.name.toLowerCase().includes(searchLower) ||
          (t.context && t.context.notes && t.context.notes.toLowerCase().includes(searchLower))
        );
      }

      if (filters.organizationId) {
        const oid = filters.organizationId;
        tasks = tasks.filter((t) => {
          const taskOrg = t.organizationId ?? (t.context && t.context.organizationId) ?? null;
          return taskOrg === oid;
        });
      }
      
      return tasks;
    } catch (error) {
      console.error('Error fetching all tasks:', error);
      throw error;
    }
  }
  
  // ==================== STATISTICS ====================
  
  /**
   * Get task statistics
   */
  async getTaskStatistics(filters = {}) {
    try {
      const tasks = await this.getAllTasks(filters);
      
      const stats = {
        total: tasks.length,
        pending: tasks.filter(t => t.status === 'assigned' || t.status === 'pending_assignment').length,
        inProgress: tasks.filter(t => t.status === 'in_progress').length,
        completed: tasks.filter(t => t.status === 'completed').length,
        overdue: tasks.filter(t => t.status === 'overdue').length,
        cancelled: tasks.filter(t => t.status === 'cancelled').length,
        
        avgComplianceScore: 0,
        avgRPN: 0,
        totalDeviations: 0
      };
      
      const completedTasks = tasks.filter(t => t.complianceScore !== null);
      if (completedTasks.length > 0) {
        stats.avgComplianceScore = Math.round(
          completedTasks.reduce((sum, t) => sum + t.complianceScore, 0) / completedTasks.length
        );
      }
      
      if (tasks.length > 0) {
        stats.avgRPN = Math.round(
          tasks.reduce((sum, t) => sum + (t.currentRPN || 0), 0) / tasks.length
        );
        
        stats.totalDeviations = tasks.reduce((sum, t) => sum + (t.deviations?.length || 0), 0);
      }
      
      return stats;
    } catch (error) {
      console.error('Error calculating task statistics:', error);
      throw error;
    }
  }
  
  // ==================== AUDIT TRAIL ====================
  
  /**
   * Log task execution action
   */
  async logAction(taskId, userId, action, protocolIndex, stepIndex, data) {
    try {
      await addDoc(this.logsCollection, {
        taskId,
        userId,
        action,
        timestamp: Timestamp.now(),
        protocolIndex,
        stepIndex,
        data
      });
    } catch (error) {
      console.error('Error logging action:', error);
      // Don't throw - logging failure shouldn't break task execution
    }
  }
  
  /**
   * Get execution log for a task
   */
  async getTaskLog(taskId) {
    try {
      const q = query(
        this.logsCollection,
        where('taskId', '==', taskId),
        orderBy('timestamp', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching task log:', error);
      throw error;
    }
  }
  
  // ==================== HELPER METHODS ====================
  
  /**
   * Get risk level from RPN
   */
  getRiskLevelFromRPN(rpn) {
    if (rpn > 400) return 'critical';
    if (rpn > 200) return 'high';
    if (rpn > 100) return 'medium';
    if (rpn > 50) return 'low';
    return 'minimal';
  }
  
  /**
   * Check if task is overdue
   */
  isTaskOverdue(task) {
    if (task.status === 'completed' || task.status === 'cancelled') {
      return false;
    }
    
    const now = Date.now();
    const scheduledEnd = task.scheduledEnd.toMillis ? task.scheduledEnd.toMillis() : task.scheduledEnd.getTime();
    
    return now > scheduledEnd;
  }
  
  /**
   * Get task progress percentage
   */
  getTaskProgress(task) {
    let totalSteps = 0;
    let completedSteps = 0;
    
    task.protocols.forEach(protocol => {
      totalSteps += protocol.steps.length;
      completedSteps += protocol.steps.filter(s => s.isComplete || s.isSkipped).length;
    });
    
    if (totalSteps === 0) return 0;
    return Math.round((completedSteps / totalSteps) * 100);
  }
  
  // ==================== TASK PACKAGE GENERATION ====================
  
  /**
   * Generate task package from protocol
   * Creates multiple coordinated tasks based on protocol's task package definition
   */
  async generateTaskPackageFromProtocol(protocolId, context, scheduledStart, userId) {
    try {
      const protocol = await protocolService.getProtocol(protocolId);
      if (!protocol) throw new Error('Protocol not found');
      
      if (!protocol.taskPackage || !protocol.taskPackage.enabled) {
        throw new Error('Protocol does not have a task package defined');
      }
      
      const packageId = this.generateId('package');
      const now = Timestamp.now();
      const baseTime = scheduledStart || now;
      
      const taskIds = [];
      
      // Generate each task in the package
      for (const taskDef of protocol.taskPackage.tasks) {
        try {
          // Calculate scheduled start based on offset
          const taskStartTime = new Date(baseTime.toMillis ? baseTime.toMillis() : baseTime.getTime());
          if (taskDef.startOffset) {
            const offsetMinutes = taskDef.startOffsetUnit === 'hours' 
              ? taskDef.startOffset * 60 
              : taskDef.startOffset;
            taskStartTime.setMinutes(taskStartTime.getMinutes() + offsetMinutes);
          }
          
          // Generate task from template
          const taskData = await taskTemplateService.generateTaskFromTemplate(
            taskDef.taskTemplateId,
            {
              ...context,
              ...taskDef.contextMapping
            },
            taskDef.assignedTo || '',
            taskStartTime,
            userId
          );
          
          // Add package metadata
          taskData.packageId = packageId;
          taskData.packageName = `${protocol.name} - Task Package`;
          taskData.sequenceOrder = taskDef.sequenceOrder;
          taskData.name = taskDef.taskName || taskData.name;
          
          // Set assignment based on task definition
          if (taskDef.assignmentType === 'group_based' && taskDef.assignedToGroup) {
            taskData.assignedToGroup = taskDef.assignedToGroup;
            taskData.assignmentType = 'group_based';
          }
          
          // Create task instance
          const taskId = await addDoc(this.tasksCollection, taskData);
          taskIds.push(taskId.id);
          
          // Log task creation
          await this.logAction(taskId.id, userId, 'CREATE_TASK', null, null, {
            packageId,
            protocolId,
            sequenceOrder: taskDef.sequenceOrder
          });
          
        } catch (error) {
          console.error(`Error generating task ${taskDef.sequenceOrder}:`, error);
          // Continue with other tasks even if one fails
        }
      }
      
      console.log(`✅ Generated task package: ${packageId} with ${taskIds.length} tasks`);
      
      return {
        packageId,
        taskIds,
        taskCount: taskIds.length
      };
    } catch (error) {
      console.error('Error generating task package:', error);
      throw error;
    }
  }
  
  /**
   * Generate ID for packages
   */
  generateId(prefix) {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

/**
 * Completează un task de tip 'atp_check'.
 * Creează atp_record cu source preluată din task (risk_map, audit, workflow etc.),
 * populează layer_5 dacă task-ul e dintr-un workflow, marchează completed.
 */
export async function completeATPTask(taskId, resultData, userId) {
  const taskRef = doc(db, 'task_instances', taskId);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) throw new Error(`Task ${taskId} not found`);
  const task = { id: taskSnap.id, ...taskSnap.data() };

  if (task.type !== 'atp_check') throw new Error(`Task ${taskId} nu este de tip atp_check`);

  const orgId = task.organization_id;
  const atpContext = task.atp_context || {};

  const atpResult = await atpRecordService.createATPRecord(orgId, {
    department_id: atpContext.department_id || null,
    department_name: atpContext.department_name || '',
    room_id: atpContext.room_id || null,
    room_name: atpContext.room_name || '',
    surface_type_id: atpContext.surface_type_id || null,
    surface_type_name: atpContext.surface_type_name || '',
    operator_id: userId,
    operator_name: resultData.operator_name || '',
    value: resultData.value,
    description: resultData.description || '',
    interpretation: resultData.interpretation,
    workflow_execution_id: task.workflow_execution_id || null,
    workflow_node_id: task.workflow_node_id || null,
    source: task.source || 'planned_ipc',
    source_id: task.source_id || null,
    source_ref: task.source_ref || null
  }, userId);

  if (task.workflow_execution_id) {
    const atpCheck = {
      atp_record_id: atpResult.id,
      atp_id: atpResult.atp_id,
      room_id: atpContext.room_id,
      surface_type: atpContext.surface_type_name,
      value: resultData.value ?? null,
      interpretation: resultData.interpretation,
      recorded_at: new Date(),
      operator_id: userId
    };
    populateATPResult(task.workflow_execution_id, [atpCheck])
      .catch(err => console.warn('[completeATPTask] populateATPResult failed:', err));
  }

  await updateDoc(taskRef, {
    status: 'completed',
    actualEnd: serverTimestamp(),
    atp_record_id: atpResult.id,
    updated_at: serverTimestamp(),
    updated_by: userId
  });

  const workflowExecId = task.workflow_execution_id || task.triggeredByWorkflow;
  if (workflowExecId) {
    try {
      const { checkAndResumeWorkflowExecution } = await import('./workflowExecutionService');
      checkAndResumeWorkflowExecution(workflowExecId, userId)
        .catch(err => console.warn('[completeATPTask] checkAndResumeWorkflowExecution failed:', err));
    } catch (e) {
      console.warn('[completeATPTask] Could not import workflowExecutionService:', e);
    }
  }

  return atpResult;
}

export const taskService = new TaskService();

